import { useCallback, useEffect } from 'react'
import { Modal as BootstrapModal } from 'react-bootstrap'

import { Modal } from '../Modal/Modal'
import { Button } from '../Button/Button'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { resetFileBrowser, selectFileBrowserBreadcrumbs, selectFileBrowserIsAtRoot } from '../../store/documentFileBrowser/documentFileBrowserSlice'
import { type IntegrationServiceId, getIntegrationDisplayName } from '../../common/integrations'
import { type DocumentConnectorIntegration } from '../../store/documentConnectors/documentConnectors'
import { IntegrationFileBrowser } from './IntegrationFileBrowser'
import styles from './LinkFolderModal.module.scss'

interface LinkFolderModalProps {
  show: boolean
  onHide: () => void
  serviceId: IntegrationServiceId
  contactName: string
  onFolderSelected: (folder: { id: string; name: string; integration?: DocumentConnectorIntegration }) => void
  initialFolderIds?: string[]
  initialFoldersReady?: boolean
}

export const LinkFolderModal = ({ show, onHide, serviceId, contactName, onFolderSelected, initialFolderIds, initialFoldersReady }: LinkFolderModalProps) => {
  const dispatch = useAppDispatch()
  const breadcrumbs = useAppSelector(selectFileBrowserBreadcrumbs)
  const isAtRoot = useAppSelector(selectFileBrowserIsAtRoot)

  const displayName = getIntegrationDisplayName(serviceId)

  const currentFolderId = isAtRoot ? null : (breadcrumbs[breadcrumbs.length - 1]?.id ?? null)
  const currentFolderName = isAtRoot ? displayName : (breadcrumbs[breadcrumbs.length - 1]?.name ?? displayName)
  const currentFolderIntegration = isAtRoot ? undefined : breadcrumbs[breadcrumbs.length - 1]?.integration

  useEffect(() => {
    return () => {
      dispatch(resetFileBrowser())
    }
  }, [dispatch])

  const handleConfirm = useCallback(() => {
    if (!currentFolderId) {
      return
    }
    onFolderSelected({ id: currentFolderId, name: currentFolderName, integration: currentFolderIntegration })
    onHide()
  }, [currentFolderId, currentFolderName, currentFolderIntegration, onFolderSelected, onHide])

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName={styles.linkFolderModal}
      bodyClassName={styles.linkFolderBody}
      header={<BootstrapModal.Title>Link Folder to {contactName}</BootstrapModal.Title>}
      body={<IntegrationFileBrowser serviceId={serviceId} showDisabledFiles initialFolderIds={initialFolderIds} initialFoldersReady={initialFoldersReady} />}
      footer={
        <>
          <Button variant='secondaryGhostButton' onClick={onHide}>
            Cancel
          </Button>
          <Button variant='primary' onClick={handleConfirm} disabled={!currentFolderId}>
            Link &ldquo;{currentFolderName}&rdquo;
          </Button>
        </>
      }
    />
  )
}
